import { useEffect, useReducer } from "react";

import { useNavigate, useSearchParams } from "react-router";

import { buildUrl } from "@/components/helpers/url-utils";
import { useAuth } from "@/components/lib/auth";
import { GetCoachingProgramsQuery } from "@/graphql";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Session = Program["sessions"][number];
type Product = Session["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

/**
 * State interface for the scheduling system.
 * Manages the complex state needed for booking coaching sessions,
 * including different booking modes (program vs independent sessions).
 */
export interface ScheduleState {
  // Selection state - what the user has chosen
  selectedTimeslots: string[]; // Array of selected time slots for booking
  selectedProduct: Product | null; // The coaching product/service being booked
  selectedCoachProduct: CoachProduct | null; // The coach product associated with the selected product
  selectedSession: Session | null; // Specific session within a program
  selectedClientId: string | null; // Client being scheduled (for coaches scheduling on behalf of clients)
  selectedSpecialtyId: string | null; // Coaching specialty filter
  selectedProgramId: string | null; // Selected coaching program
  selectedWorkspaceId: string | null; // Selected workspace for booking as a coach

  // UI state - controls what's visible and interaction state
  showAllTimes: boolean; // Whether to show all available times or filtered subset
  isSubmitting: boolean; // Loading state during form submission
  showProducts: boolean; // Whether to show product selection interface
  showPrograms: boolean; // Whether to show program selection interface
  tab: "program" | "independent"; // Current booking mode

  // Navigation and messaging
  backUrl: string | null; // URL to return to after booking completion
  errorMessage: string | null; // Error message to display to user
  warningMessage: string | null; // Warning message to display to user

  // Advanced scheduling features
  preferredTimeslots: string[]; // User's preferred time slots for matching
  programsInitialized: boolean; // Whether program data has been loaded
}

/**
 * Action types for the schedule reducer.
 * Each action represents a specific state change in the scheduling flow.
 */
type ScheduleAction =
  // Selection actions - user making choices
  | { type: "SELECT_TIMESLOTS"; payload: string[] }
  | {
      type: "SELECT_PRODUCT";
      payload: { product: Product | null; coachProduct: CoachProduct | null };
    }
  | { type: "SELECT_SESSION"; payload: Session | null }
  | { type: "SELECT_CLIENT"; payload: string | null }
  | { type: "SELECT_SPECIALTY"; payload: string | null }
  | { type: "SELECT_PROGRAM"; payload: string | null }
  | { type: "SELECT_WORKSPACE"; payload: string | null }

  // UI state actions - controlling interface behavior
  | { type: "SHOW_ALL_TIMES"; payload: boolean }
  | { type: "IS_SUBMITTING"; payload: boolean }
  | { type: "SHOW_PRODUCTS"; payload: boolean }
  | { type: "SHOW_PROGRAMS"; payload: boolean }

  // Utility actions - resetting and messaging
  | { type: "SET_ERROR_MESSAGE"; payload: string | null }
  | { type: "SET_WARNING_MESSAGE"; payload: string | null }
  | { type: "SET_PREFERRED_TIMESLOTS"; payload: string[] }
  | { type: "SET_PROGRAMS_INITIALIZED"; payload: boolean }

  // Mode switching - changing between booking types
  | { type: "CHANGE_TAB"; payload: "program" | "independent" };

/**
 * Reducer function that handles all state updates for the scheduling system.
 * Implements complex business logic for state transitions.
 */
const scheduleReducer = (
  state: ScheduleState,
  action: ScheduleAction,
): ScheduleState => {
  switch (action.type) {
    case "SELECT_TIMESLOTS":
      return {
        ...state,
        selectedTimeslots: action.payload,
        errorMessage: null, // Clear errors when user makes new selection
      };

    case "SELECT_PRODUCT":
      return {
        ...state,
        selectedProduct: action.payload.product,
        selectedCoachProduct: action.payload.coachProduct,
      };

    case "SELECT_SESSION":
      return {
        ...state,
        selectedSession: action.payload,
        errorMessage: null,
        warningMessage: null,
      };

    case "SELECT_CLIENT":
      return {
        ...state,
        selectedClientId: action.payload,
        warningMessage: null,
        selectedProduct: null,
        showPrograms: true,
        showProducts: true,
        programsInitialized: false,
      };

    case "SELECT_SPECIALTY":
      return { ...state, selectedSpecialtyId: action.payload };

    case "SHOW_ALL_TIMES":
      return { ...state, showAllTimes: action.payload };

    case "IS_SUBMITTING":
      return { ...state, isSubmitting: action.payload };

    case "SHOW_PRODUCTS":
      return { ...state, showProducts: action.payload };

    case "SHOW_PROGRAMS":
      return { ...state, showPrograms: action.payload };

    case "SELECT_PROGRAM":
      return { ...state, selectedProgramId: action.payload };

    case "SELECT_WORKSPACE":
      return {
        ...state,
        selectedWorkspaceId: action.payload,
        selectedProduct: null,
        selectedCoachProduct: null,
        showPrograms: true,
        showProducts: true,
        programsInitialized: false,
      };

    case "SET_PROGRAMS_INITIALIZED":
      return { ...state, programsInitialized: action.payload };

    case "SET_ERROR_MESSAGE":
      return { ...state, errorMessage: action.payload };

    case "SET_WARNING_MESSAGE":
      return { ...state, warningMessage: action.payload };

    case "SET_PREFERRED_TIMESLOTS":
      return { ...state, preferredTimeslots: action.payload };

    case "CHANGE_TAB":
      // Complex state transition when switching between program and independent booking modes
      return {
        ...state,
        tab: action.payload,
        showProducts: action.payload === "independent", // Only show products in independent mode
        selectedProduct: null, // Clear product selection when switching modes
        programsInitialized: action.payload === "independent", // Mark as initialized for independent mode
        selectedSession: null, // Clear session selection when switching modes
      };

    default:
      return state;
  }
};

/**
 * Custom React hook that manages the complex state for the scheduling interface.
 *
 * This hook handles:
 * - State management for booking coaching sessions
 * - Two booking modes: program sessions vs independent sessions
 * - URL parameter synchronization for deep linking and browser back/forward
 * - Dynamic UI state based on user permissions and current selections
 *
 * The scheduling system supports multiple booking flows:
 * 1. Program mode: Booking sessions within existing coaching programs
 * 2. Independent mode: Booking standalone coaching sessions
 * 3. Match mode: Special flow for matched coaching sessions
 *
 * @returns - Object containing current state and dispatch function
 */
export function useScheduleState() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determine if this is a match booking flow (special case)
  const isMatch = searchParams.get("match") === "true";

  // Determine if we should show programs interface
  const showPrograms =
    !!searchParams.get("clientId") ||
    (!!currentUser.currentWorkspace?.id &&
      !!currentUser.participantProgramIds?.length &&
      searchParams.get("programComponentId") === null);

  // Initialize state with values from URL parameters for deep linking support
  const [state, dispatch] = useReducer(scheduleReducer, {
    selectedTimeslots: [],
    selectedProduct: null,
    selectedCoachProduct: null,
    selectedSession: null,
    // Initialize from URL params for deep linking
    selectedClientId: searchParams.get("clientId") || null,
    selectedSpecialtyId: searchParams.get("specialtyId") || null,
    selectedProgramId: null,
    selectedWorkspaceId: searchParams.get("workspaceId") || null,
    showAllTimes: false,
    isSubmitting: false,
    // Show products interface if no specific program component is selected
    showProducts: searchParams.get("programComponentId") === null,
    showPrograms,
    tab: "program", // Default to program mode
    backUrl: searchParams.get("back") || null, // URL to return to after completion
    errorMessage: null,
    warningMessage: null,
    preferredTimeslots: [],
    programsInitialized: false,
  });

  /**
   * URL synchronization effect.
   * Keeps the browser URL in sync with the current state for:
   * - Deep linking support
   * - Browser back/forward navigation
   * - Sharing specific booking states
   *
   * Updates URL whenever key state changes occur without page reload.
   */
  useEffect(() => {
    navigate(
      buildUrl(window.location.pathname, {
        data: {
          // Preserve existing productId from URL if no product selected yet
          productId: state.selectedProduct?.id || searchParams.get("productId"),
          specialtyId: state.selectedSpecialtyId,
          clientId: state.selectedClientId,
          sessionId: state.selectedSession?.id,
          match: isMatch ?? undefined,
          // Preserve programComponentId for direct session links
          programComponentId: searchParams.get("programComponentId"),
          workspaceId: state.selectedWorkspaceId,
          back: state.backUrl,
        },
      }),
      { replace: true }, // Use replace to avoid cluttering browser history
    );
  }, [
    state.selectedClientId,
    state.selectedProduct,
    state.selectedSpecialtyId,
    state.selectedSession,
    state.selectedWorkspaceId,
    navigate,
    searchParams,
    isMatch,
  ]);

  return { state, dispatch };
}
